import React, { useEffect, useRef, useState } from 'react';
import { FilterProps } from './types';
import styles from './Filter.module.scss';
import Typography from '@primitives/Typography';
import SelectPicker from '@components/SelectPicker';
import { SelectPickerOptionProps, SelectPickerValue } from '@components/SelectPicker/types';
import ArrowDownSolidIcon from '@icons/ArrowDownSolidIcon';
import ArrowUpSolidIcon from '@icons/ArrowUpSolidIcon';
import CancelIcon from '@icons/CancelIcon';
import { useOutsideClick, useKeyListener } from '@hooks';
import cx from 'classnames';
import { ESCAPE_KEY } from '@constants/Components';

const Filter: React.FC<FilterProps> = ({
    title = 'Filter',
    options = [],
    selectedValues,
    onSelectionChange,
    filterIcon,
    isSingleSelect = false,
    containerClass = '',
    containerAppliedClass = '',
    filterClass = '',
    titleClass = '',
    pickerWrapperClass = '',
    iconContainerClass = '',
    onCancelClick = null,
    resetSelectedFilters = false,
    filterPositionClass = '',
    onClick,
    disabled,
    clearAll = true,
    error = '',
    errorMsgIcon = null,
    showOutline = false,
    removeFilterCallback,
    selectedValue = '',
    ...restProps
}) => {
    const [showPicker, setShowPicker] = useState<boolean>(false);
    const finalSelectedValue = isSingleSelect && !selectedValue ? [] : [selectedValue];
    const [selectedFilters, setSelectedFilters] = useState<Array<SelectPickerValue>>(
        selectedValues || finalSelectedValue || [],
    );
    const filterRef = useRef(null);
    const selectedFiltersWhenPickerIsOpened = useRef<SelectPickerOptionProps[]>([]);

    useEffect(() => {
        if (showPicker) {
            selectedFiltersWhenPickerIsOpened.current = options.filter((option) =>
                selectedFilters.includes(option.value),
            );
        }
    }, [showPicker]);

    useEffect(() => {
        if (resetSelectedFilters) {
            setSelectedFilters([]);
            onSelectionChange([]);
            selectedFiltersWhenPickerIsOpened.current = [];
        }
    }, [resetSelectedFilters]);

    const togglePicker = (event: React.MouseEvent<HTMLDivElement> | undefined = undefined) => {
        setShowPicker(!showPicker);
        if (onClick && event) {
            onClick(event);
        }
    };

    const onFilterChangeHandler = (val: SelectPickerOptionProps[] | SelectPickerOptionProps) => {
        if (Array.isArray(val)) {
            const selectedOptions = val.map((option) => option?.value);
            setSelectedFilters(selectedOptions);
        } else if (val && typeof val === 'object') {
            setShowPicker(false);
            setSelectedFilters([val?.value]);
            onSelectionChange(val);
        }
    };

    const removeFilters = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedFilters([]);
        onSelectionChange([]);
        selectedFiltersWhenPickerIsOpened.current = [];
        if (removeFilterCallback) {
            removeFilterCallback();
        }
    };

    const handleCancel = (): void => {
        setShowPicker(false);
        setSelectedFilters(
            selectedFiltersWhenPickerIsOpened.current.map((selection) => selection.value),
        );
        if (onCancelClick) {
            onCancelClick();
        }
    };

    const handleApply = (values: SelectPickerOptionProps[]): void => {
        togglePicker();
        onSelectionChange(values);
    };

    const getErrorMessage = (): JSX.Element | null => {
        if (error && !showPicker)
            return (
                <div className={styles['filter-error']}>
                    {errorMsgIcon && <div className={styles['error-icon']}>{errorMsgIcon}</div>}
                    <Typography as="span" variant="p4" className={styles['error-text']}>
                        {error}
                    </Typography>
                </div>
            );

        return null;
    };

    useOutsideClick(filterRef, () => {
        if (showPicker) {
            // Only removing the picker incase of single select prop is true.
            if (isSingleSelect) {
                setShowPicker(false);
                return;
            }
            handleCancel();
        }
    });

    useKeyListener(ESCAPE_KEY, () => setShowPicker(false), showPicker);

    const ArrowIcon = showPicker ? ArrowUpSolidIcon : ArrowDownSolidIcon;

    /**
     * SingleSelect condition should not have been there in Filters - this was added just to use it as a single select dropdown.
     * So adding clearAll condition to fix this and provide backward compatibility also.
     */
    const isShowCancelOption = !!selectedFilters.length && (!isSingleSelect || clearAll);

    const appliedClass = cx(styles['filter--applied'], containerAppliedClass);

    return (
        <div
            className={cx(
                styles['filter-container'],
                {
                    [styles['filter-container__disabled']]: disabled,
                },
                containerClass,
            )}
            ref={filterRef}
            data-testid="filter-container"
            role="presentation"
        >
            <div
                className={cx(
                    styles['filter'],
                    {
                        [appliedClass]: selectedFilters.length > 0,
                        [styles['filter--error']]: error,
                        [styles['filter--active']]: showPicker,
                        [styles['filter--disabled']]: disabled,
                        [styles['filter--outline']]: showOutline,
                    },
                    filterClass,
                )}
                onClick={togglePicker}
                data-testid="filter-dropdown-toggler"
            >
                {filterIcon}
                <Typography
                    variant="p3"
                    className={cx(
                        styles['filter--title'],
                        {
                            [styles['filter--title--active']]: selectedFilters.length > 0,
                            [styles['filter--title--disabled']]: disabled,
                        },
                        titleClass,
                    )}
                >
                    {title}{' '}
                    {!isSingleSelect && selectedFilters.length > 0 && `(${selectedFilters.length})`}
                </Typography>
                <div className={iconContainerClass}>
                    <ArrowIcon width={9} />
                </div>
                {isShowCancelOption && <CancelIcon width={16} onClick={removeFilters} />}
            </div>
            {getErrorMessage()}
            {showPicker && (
                <SelectPicker
                    multiSelect={!isSingleSelect}
                    options={options}
                    selectedValues={selectedFilters}
                    selectedValue={selectedFilters?.[0]}
                    onSelectionChange={onFilterChangeHandler}
                    wrapperClasses={cx(styles['picker'], pickerWrapperClass, filterPositionClass)}
                    handleApplyClick={handleApply}
                    handleCancelClick={handleCancel}
                    data-testid="filter-dropdown"
                    {...restProps}
                />
            )}
        </div>
    );
};

export default Filter;
